import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import type {
	ExternalEnrichmentDto,
	PersistedLookupRoundDto,
} from '../dto/external-lookup.dto';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { CrossReferenceRequestLineEntity } from './cross-reference-request-line.entity';
import type { ItemCrossReferenceKind } from './item-cross-reference.entity';

/**
 * What a stored reference article is keyed on.
 *
 * Every `ItemCrossReferenceKind`, plus `description`: a line that carries no
 * article number at all is still an article somebody asked for, and keying it
 * on its own folded wording is the only identity it has. That extra kind is
 * deliberately NOT part of `ItemCrossReferenceKind` — a description is not a
 * durable equivalence a person could confirm ("50 mm hex bolt" names a shape,
 * not a part), so it must never reach the learning table.
 */
export type CrossReferenceExternalArticleIdentifierKind =
	| ItemCrossReferenceKind
	| 'description';

/**
 * What an external lookup once established about ONE article identifier — the
 * reference-article store.
 *
 * The `external_lookup` step pays a live web search per line. Without this
 * table the same customer part number arriving on a second request pays for the
 * same research again, which is the single largest avoidable cost in the
 * pipeline. A row here is that research, kept so the next line carrying the
 * same identifier gets it for nothing.
 *
 * It is a CACHE OF EVIDENCE, never a decision. The stored envelope is the same
 * validated enrichment the live path produces, and it re-enters a line through
 * the same fill-only-if-absent merge, so nothing reused here can do anything a
 * fresh lookup could not (design §1.2, §6.1). In particular it carries no item
 * id, no score and no confidence.
 *
 * Scope follows `ItemCrossReferenceKind`: a `customer` number means "what THIS
 * customer calls this article" and is stored against the client, while
 * `competitor`, `norm` and `unknown` numbers are facts about the article itself
 * and are stored globally with a null client. A `description` row is global for
 * the same reason: the wording is the article's own, not one customer's private
 * number for it.
 *
 * The unique key CANNOT be relied on to deduplicate the global rows — MySQL
 * treats NULLs as distinct in a unique index, so two null-client rows for the
 * same identifier would both be admitted. Every write therefore looks the row
 * up first, exactly as `item_cross_reference` persistence does.
 */
@Index('idx_crea_identifier', ['identifierKind', 'identifierNormalized'])
@Index(
	'uq_crea_identity',
	['identifierKind', 'identifierNormalized', 'fkClientId'],
	{ unique: true },
)
@Entity('cross_reference_external_article')
export class CrossReferenceExternalArticleEntity extends BaseEntity {
	/**
	 * The identifier exactly as the source line printed it.
	 *
	 * Carried alongside the fold because the fold is lossy and unreadable:
	 * "471234" is not what a person searching for "47-1234" types, and the
	 * references screen has to show them the number they recognise.
	 */
	@Column('varchar', { name: 'identifierValue', length: 128 })
	public identifierValue!: string;

	/**
	 * Which kind of number this is, same vocabulary as a line's article numbers,
	 * plus `description` for a line that printed no number at all. The column is
	 * a varchar, so the wider vocabulary needs no migration.
	 */
	@Column('varchar', { name: 'identifierKind', length: 16 })
	public identifierKind!: CrossReferenceExternalArticleIdentifierKind;

	/**
	 * The comparison key, folded with the SAME `normalizeIdentifier` the engine,
	 * `item_cross_reference` and the feedback events use. One fold definition,
	 * never two — a second fold would mean rows that are written but never read.
	 */
	@Column('varchar', { name: 'identifierNormalized', length: 128 })
	public identifierNormalized!: string;

	/**
	 * Client scope. Set ONLY for `customer` numbers; null for the kinds that
	 * describe the article rather than one customer's name for it.
	 */
	@Column('int', { name: 'fk_client__id', nullable: true, unsigned: true })
	public fkClientId!: number | null;

	/**
	 * The description of the line whose lookup produced this row. Carried purely
	 * so a person browsing the table can tell what an identifier refers to;
	 * nothing reads it.
	 */
	@Column('varchar', { name: 'description', nullable: true, length: 500 })
	public description!: string | null;

	/**
	 * The validated synthesis envelope, exactly as it was applied to the source
	 * line. Stored verbatim so a reuse merges precisely what the original line
	 * merged.
	 */
	@Column('json', { name: 'enrichment' })
	public enrichment!: ExternalEnrichmentDto;

	/**
	 * The per-source rounds the lookup recorded, so the review panel's source
	 * cards and the exclude/restore recompute work on a reused line as well as on
	 * a freshly searched one. Null for a lookup that recorded no rounds.
	 */
	@Column('json', { name: 'enrichmentRounds', nullable: true })
	public enrichmentRounds!: Array<PersistedLookupRoundDto> | null;

	/**
	 * When the web research actually ran. The freshness cutoff is measured
	 * against this rather than against `_modified_date`, which any unrelated
	 * write would move.
	 */
	@Column('datetime', { name: 'lookedUpAt' })
	public lookedUpAt!: Date;

	/** Free text a person adds on the reference product page. */
	@Column('text', { name: 'notes', nullable: true })
	public notes!: string | null;

	/** Provenance: the line whose lookup produced this row. */
	@Column('int', {
		name: 'fk_cross_reference_request_line__id',
		nullable: true,
		unsigned: true,
	})
	public fkCrossReferenceRequestLineId!: number | null;

	@ManyToOne(() => ClientEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity | null;

	// SET NULL rather than CASCADE: the research outlives the line that paid for
	// it, and losing a cached answer because a request was cleaned up would put
	// the cost straight back.
	@ManyToOne(() => CrossReferenceRequestLineEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_cross_reference_request_line__id', referencedColumnName: 'id' },
	])
	public fkCrossReferenceRequestLine!: CrossReferenceRequestLineEntity | null;
}
